import * as React from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import { useLocation, useNavigate } from "react-router-dom";
import { DARKCOLORS, LIGHTCOLORS, ROUTES } from "../../../Utils/constants";
import "./Sidebar.css";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Collapse,
  Box,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import { useThemeContext } from "../../../Context/ThemeContext";
import { useTranslation } from "react-i18next";
import {
  HomeIcon,
  AnalyticsIcon,
  TransactionsIcon,
  VendingMachineIcon,
  InventoryIcon,
  AdvertisementIcon,
  MembershipIcon,
  DiscountIcon,
  CouponIcon,
  ReservationIcon,
  ReturnIcon,
  UserIcon,
  MaintenanceIcon,
  HelpIcon,
  TaxIcon,
  SpareIcon,
  ServiceIcon,
  ManageIcon,
  ViewIcon,
  FranchiseIcon,
  Academy,
} from "../CustomIcons";
import useAuth from "../../../Hooks/useAuth";
import { getFranchiseMachineType } from "../../../Services/authService";

const drawerWidth = 230;

//const showSpares = !user?.roles?.includes("ROLE_SUPER_ADMIN");

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8.5)} + 1px)`,
  },
  "& .MuiListItemButton-root": {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function Sidebar({ open }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { mode } = useThemeContext();
  const { t } = useTranslation();
  const [machineType, setMachineType] = useState("NONE");

  const isBoth = machineType === "BOTH";
  const isReturnOnly = machineType === "RETURN_ONLY";
  const isVendingOnly = machineType === "VENDING_ONLY";
  const isReturnFoodVending = machineType === "RETURN_AND_FOOD_VENDING";

  const showSpares = !user?.roles?.includes("ROLE_SUPER_ADMIN");
  const maintenanceRoutes = [
    ROUTES.SERVICE_REQUEST,
    ROUTES.COMPLAINTS,
    ...(showSpares
      ? [ROUTES.SPARES, ROUTES.CARTPAGE, ROUTES.ORDERS, ROUTES.ORDER_DETAILS]
      : []),
  ];

const showVendingMachines = isBoth || isVendingOnly || isReturnFoodVending;

const showReturns = isReturnFoodVending;
 

  const [openView, setOpenView] = useState(false);
  const [openManage, setOpenManage] = useState(false);
  const [openMaintenance, setOpenMaintenance] = useState(false);

  const sidebarBackground = mode === "dark" ? "#2D2D2D" : "#ffffff";
  const iconSidebarBackground = mode === "dark" ? "#1B1B1B" : "#ECEDF8";
  const blueColor =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;

  // Expand/collapse submenus based on the active route
  React.useEffect(() => {
    if (
      location.pathname.includes(ROUTES.SALES_ANALYTICS) ||
      location.pathname.includes(ROUTES.TRANSACTIONS)
    ) {
      setOpenView(true);
    }
    if (
      location.pathname.includes(ROUTES.VENDING_MACHINES) ||
      location.pathname.includes(ROUTES.ADD_MACHINE) ||
      location.pathname.includes(ROUTES.MACHINE_DETAILS) ||
      location.pathname.includes(ROUTES.INVENTORY) ||
      location.pathname.includes(ROUTES.ADVERTISEMENTS) ||
      location.pathname.includes(ROUTES.ADD_ADVERTISEMENT) ||
      location.pathname.includes(ROUTES.MEMBERSHIP_CARDS) ||
      location.pathname.includes(ROUTES.DISCOUNTS) ||
      location.pathname.includes(ROUTES.COUPONS) ||
      location.pathname.includes(ROUTES.USERS) ||
      location.pathname.includes(ROUTES.RESERVATIONS) ||
      location.pathname.includes(ROUTES.RETURNS)
    ) {
      setOpenManage(true);
    }
    if (
      location.pathname.includes(ROUTES.SERVICE_REQUEST) ||
      location.pathname.includes(ROUTES.COMPLAINTS) ||
      location.pathname.includes(ROUTES.SPARES) ||
      location.pathname.includes(ROUTES.CARTPAGE) ||
      location.pathname.includes(ROUTES.ORDERS) ||
      location.pathname.includes(ROUTES.ORDER_DETAILS)
    ) {
      setOpenMaintenance(true);
    }
  }, [location]);

  React.useEffect(() => {
    const fetchMachineType = async () => {
      try {
        const response = await getFranchiseMachineType();

        // console.log("FULL API RESPONSE:", response);
        // console.log("API DATA:", response?.data);
        // console.log("MACHINE TYPE:", response?.data?.machineType);

        setMachineType(response?.data?.machineType || "NONE");
      } catch (error) {
        console.error("Failed to fetch franchise machine type:", error);
        setMachineType("NONE");
      }
    };

    fetchMachineType();
  }, []);

  // Function to check if the route is active
  const isActive = (route) => location.pathname === route;
  const isParentActive = (routes) =>
    routes.some((route) => location.pathname.includes(route));

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          "& .MuiDrawer-paper": {
            height: "89.8vh",
            top: "92px",
            backgroundColor: "transparent",
            borderRight: "none",
          },
        }}
      >
        <List sx={{ marginLeft: 2.5, paddingTop: 0, pr: "2px" }}>
          {/* Home Section */}
          <ListItem
            className="sidebar-section-list"
            sx={{
              backgroundColor: sidebarBackground,
              ...(!open && { borderRadius: "8px", margin: "0 0px" }),
            }}
            disablePadding
            onClick={() => navigate(ROUTES.HOME)}
          >
            <ListItemButton
              sx={{
                ...(open
                  ? {
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                      padding: "4px 16px",
                    }
                  : {
                      minHeight: 40,
                      padding: "4px 0",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                    }),
              }}
              className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed" : ""} ${isActive(ROUTES.HOME) ? "active" : ""}`}
            >
              <ListItemIcon
                className="sidebar-navicons-iconroot"
                sx={
                  !open
                    ? {
                        minWidth: 0,
                        justifyContent: "center",
                        marginBottom: "2px",
                      }
                    : {}
                }
              >
                <HomeIcon
                  color={isActive(ROUTES.HOME) ? blueColor : "#6F6F6F"}
                />
              </ListItemIcon>
              <h2
                className={
                  open
                    ? "sidebar-navitems-main"
                    : "sidebar-navitems-main-closed"
                }
                style={{
                  color: isActive(ROUTES.HOME) ? blueColor : "",
                  fontFamily: "Satoshi",
                  ...(open
                    ? {
                        fontFamily: "Satoshi",
                      }
                    : {
                        fontSize: "10px",
                        marginTop: "4px",
                        textAlign: "center",
                        fontFamily: "Satoshi",
                        marginBottom: "2px",
                      }),
                }}
              >
                {t("sidebar.home")}
              </h2>
            </ListItemButton>
          </ListItem>

          {/* View Section */}
          {!user?.roles?.includes("ROLE_EMPLOYEE") && (
            <List
              sx={{
                backgroundColor: sidebarBackground,
                ...(!open && { borderRadius: "8px", margin: "0 0px" }),
              }}
              className={`sidebar-section-list ${isParentActive([ROUTES.SALES_ANALYTICS, ROUTES.TRANSACTIONS]) ? "active-parent" : ""}`}
            >
              <ListItem
                disablePadding
                onClick={() => {
                  setOpenView(!openView);
                }}
              >
                <ListItemButton
                  sx={{
                    ...(open
                      ? {
                          "&:hover": {
                            backgroundColor: "transparent",
                            opacity: 1,
                          },
                          padding: "4px 16px",
                        }
                      : {
                          minHeight: 56,
                          padding: "4px 0",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          "&:hover": {
                            backgroundColor: "transparent",
                            opacity: 1,
                          },
                        }),
                  }}
                  className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed-dropdown" : ""} ${isParentActive([ROUTES.SALES_ANALYTICS, ROUTES.TRANSACTIONS]) ? "active-parent" : ""}`}
                >
                  <ListItemIcon
                    className="sidebar-navicons-iconroot"
                    sx={!open ? { minWidth: 0, justifyContent: "center" } : {}}
                  >
                    <ViewIcon
                      color={
                        isParentActive([
                          ROUTES.SALES_ANALYTICS,
                          ROUTES.TRANSACTIONS,
                        ])
                          ? blueColor
                          : "#6F6F6F"
                      }
                    />
                  </ListItemIcon>
                  {open ? (
                    <>
                      <h2 className="sidebar-navitems-main">
                        {t("sidebar.view")}
                      </h2>
                      {openView ? <ExpandLess /> : <ExpandMore />}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0px",
                      }}
                    >
                      <h2
                        className={
                          open
                            ? "sidebar-navitems-main"
                            : "sidebar-navitems-main-closed"
                        }
                        style={{
                          fontSize: "10px",
                          marginTop: "4px",
                          color: isParentActive([
                            ROUTES.SALES_ANALYTICS,
                            ROUTES.TRANSACTIONS,
                          ])
                            ? blueColor
                            : "",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          fontFamily: "Satoshi",
                          marginBottom: "2px",
                        }}
                      >
                        {t("sidebar.view")}
                      </h2>
                      {openView ? (
                        <ExpandLess
                          sx={{
                            fontSize: "14px",
                            marginTop: "-2px",
                            marginBottom: open ? "0px" : "0px",
                          }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{
                            fontSize: "14px",
                            marginTop: "-2px",
                            marginBottom: open ? "0px" : "0px",
                          }}
                        />
                      )}
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
              <Collapse in={openView} timeout="auto" unmountOnExit>
                <List component="div">
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.SALES_ANALYTICS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.SALES_ANALYTICS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot subiconsec-active"
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.SALES_ANALYTICS])
                              ? blueColor
                              : "transparent",
                            padding: "0px 6px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.SALES_ANALYTICS,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <AnalyticsIcon
                            color={
                              isParentActive([ROUTES.SALES_ANALYTICS])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.SALES_ANALYTICS])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.sales_analytics")}
                      </h2>
                    </ListItemButton>
                  </ListItem>

                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.TRANSACTIONS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.TRANSACTIONS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot subiconsec-active2"
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.TRANSACTIONS])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.TRANSACTIONS,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <TransactionsIcon
                            color={
                              isParentActive([ROUTES.TRANSACTIONS])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.TRANSACTIONS])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.transactions")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                </List>
              </Collapse>
            </List>
          )}

          {/* Manage Section */}
          <List
            sx={{
              backgroundColor: sidebarBackground,
              ...(!open && { borderRadius: "8px", margin: "0 0px" }),
            }}
            className={`sidebar-section-list ${isParentActive([ROUTES.VENDING_MACHINES, ROUTES.ADD_MACHINE, ROUTES.MACHINE_DETAILS, ROUTES.INVENTORY, ROUTES.ADVERTISEMENTS, ROUTES.ADD_ADVERTISEMENT, ROUTES.MEMBERSHIP_CARDS, ROUTES.DISCOUNTS, ROUTES.COUPONS, ROUTES.RESERVATIONS, ROUTES.RETURNS, ROUTES.USERS]) ? "active-parent" : ""}`}
          >
            <ListItem
              disablePadding
              onClick={() => {
                setOpenManage(!openManage);
              }}
            >
              <ListItemButton
                sx={{
                  ...(open
                    ? {
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                        padding: "4px 16px",
                      }
                    : {
                        minHeight: 56,
                        padding: "4px 0",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }),
                }}
                className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed-dropdown" : ""} ${isParentActive([ROUTES.VENDING_MACHINES, ROUTES.ADD_MACHINE, ROUTES.MACHINE_DETAILS, ROUTES.INVENTORY, ROUTES.ADVERTISEMENTS, ROUTES.ADD_ADVERTISEMENT, ROUTES.MEMBERSHIP_CARDS, ROUTES.DISCOUNTS, ROUTES.COUPONS, ROUTES.RESERVATIONS, ROUTES.RETURNS, ROUTES.USERS]) ? "active-parent" : ""}`}
              >
                <ListItemIcon
                  className="sidebar-navicons-iconroot"
                  sx={!open ? { minWidth: 0, justifyContent: "center" } : {}}
                >
                  <ManageIcon
                    color={
                      isParentActive([
                        ROUTES.VENDING_MACHINES,
                        ROUTES.ADD_MACHINE,
                        ROUTES.MACHINE_DETAILS,
                        ROUTES.INVENTORY,
                        ROUTES.ADVERTISEMENTS,
                        ROUTES.ADD_ADVERTISEMENT,
                        ROUTES.MEMBERSHIP_CARDS,
                        ROUTES.DISCOUNTS,
                        ROUTES.COUPONS,
                        ROUTES.RESERVATIONS,
                        ROUTES.RETURNS,
                        ROUTES.USERS,
                      ])
                        ? blueColor
                        : "#6F6F6F"
                    }
                  />
                </ListItemIcon>
                {open ? (
                  <>
                    <h2 className="sidebar-navitems-main">
                      {t("sidebar.manage")}
                    </h2>
                    {openManage ? <ExpandLess /> : <ExpandMore />}
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "0px",
                    }}
                  >
                    <h2
                      className={
                        open
                          ? "sidebar-navitems-main"
                          : "sidebar-navitems-main-closed"
                      }
                      style={{
                        fontSize: "10px",
                        marginTop: "4px",
                        color: isParentActive([
                          ROUTES.VENDING_MACHINES,
                          ROUTES.ADD_MACHINE,
                          ROUTES.MACHINE_DETAILS,
                          ROUTES.INVENTORY,
                          ROUTES.ADVERTISEMENTS,
                          ROUTES.ADD_ADVERTISEMENT,
                          ROUTES.MEMBERSHIP_CARDS,
                          ROUTES.DISCOUNTS,
                          ROUTES.COUPONS,
                          ROUTES.USERS,
                        ])
                          ? blueColor
                          : "",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "40px",
                        textAlign: "center",
                        fontFamily: "Satoshi",
                        marginBottom: "2px",
                      }}
                    >
                      {t("sidebar.manage")}
                    </h2>
                    {openManage ? (
                      <ExpandLess
                        sx={{
                          fontSize: "14px",
                          marginTop: "-2px",
                          marginBottom: open ? "0px" : "2px",
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          fontSize: "14px",
                          marginTop: "-2px",
                          marginBottom: open ? "0px" : "2px",
                        }}
                      />
                    )}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={openManage} timeout="auto" unmountOnExit>
              <List component="div">
                {/* Vending Machines — shown for both machine types (VENDING_ONLY, RETURN_ONLY, BOTH) */}
                {machineType !== "NONE" && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.VENDING_MACHINES)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isParentActive([ROUTES.VENDING_MACHINES, ROUTES.ADD_MACHINE, ROUTES.MACHINE_DETAILS]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot subiconsec-active"
                      >
                        <Box
                          sx={{
                            background: isParentActive([
                              ROUTES.VENDING_MACHINES,
                              ROUTES.ADD_MACHINE,
                              ROUTES.MACHINE_DETAILS,
                            ])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.VENDING_MACHINES,
                                ROUTES.ADD_MACHINE,
                                ROUTES.MACHINE_DETAILS,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <VendingMachineIcon
                            color={
                              isParentActive([
                                ROUTES.VENDING_MACHINES,
                                ROUTES.ADD_MACHINE,
                                ROUTES.MACHINE_DETAILS,
                              ])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([
                            ROUTES.VENDING_MACHINES,
                            ROUTES.ADD_MACHINE,
                            ROUTES.MACHINE_DETAILS,
                          ])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.vending_machines")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Inventory — vending-related */}
                {showVendingMachines && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.INVENTORY)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.INVENTORY) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot"
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.INVENTORY])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.INVENTORY,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <InventoryIcon
                            color={
                              isParentActive([ROUTES.INVENTORY])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.INVENTORY])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.inventory")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Advertisements — shown for both machine types (VENDING_ONLY, RETURN_ONLY, BOTH) */}
                {machineType !== "NONE" && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.ADVERTISEMENTS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isParentActive([ROUTES.ADVERTISEMENTS, ROUTES.ADD_ADVERTISEMENT]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot"
                      >
                        <Box
                          sx={{
                            background: isParentActive([
                              ROUTES.ADVERTISEMENTS,
                              ROUTES.ADD_ADVERTISEMENT,
                            ])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.ADVERTISEMENTS,
                                ROUTES.ADD_ADVERTISEMENT,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <AdvertisementIcon
                            color={
                              isParentActive([
                                ROUTES.ADVERTISEMENTS,
                                ROUTES.ADD_ADVERTISEMENT,
                              ])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([
                            ROUTES.ADVERTISEMENTS,
                            ROUTES.ADD_ADVERTISEMENT,
                          ])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.advertisements")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Membership Cards — vending-related, super-admin only */}
                {user?.roles?.includes("ROLE_SUPER_ADMIN") &&
                  showVendingMachines && (
                    <ListItem
                      disablePadding
                      onClick={() => navigate(ROUTES.MEMBERSHIP_CARDS)}
                    >
                      <ListItemButton
                        sx={{
                          pl: 4,
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "transparent",
                            opacity: 1,
                          },
                        }}
                        className={`sidebar-section-sub ${isActive(ROUTES.MEMBERSHIP_CARDS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                      >
                        <ListItemIcon
                          sx={{ background: iconSidebarBackground }}
                          className="sidebar-navicons-subiconroot"
                        >
                          <Box
                            sx={{
                              background: isParentActive([
                                ROUTES.MEMBERSHIP_CARDS,
                              ])
                                ? blueColor
                                : "transparent",
                              padding: "2px 6px 0px",
                              borderRadius: "6px",
                              transition: "background-color 0.3s ease",
                              "&:hover": {
                                backgroundColor: isParentActive([
                                  ROUTES.MEMBERSHIP_CARDS,
                                ])
                                  ? ""
                                  : "#FFF",
                              },
                            }}
                          >
                            <MembershipIcon
                              color={
                                isParentActive([ROUTES.MEMBERSHIP_CARDS])
                                  ? "#FFF"
                                  : "#6F6F6F"
                              }
                            />
                          </Box>
                        </ListItemIcon>
                        <h2
                          className="sidebar-navitems-submain"
                          style={{
                            color: isParentActive([ROUTES.MEMBERSHIP_CARDS])
                              ? blueColor
                              : "",
                          }}
                        >
                          {t("membershipCards.customers")}
                        </h2>
                      </ListItemButton>
                    </ListItem>
                  )}

                {/* Discounts — vending-related */}
                {showVendingMachines && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.DISCOUNTS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.DISCOUNTS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot"
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.DISCOUNTS])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.DISCOUNTS,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <DiscountIcon
                            color={
                              isParentActive([ROUTES.DISCOUNTS])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.DISCOUNTS])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.discounts")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Coupons — vending-related */}
                {showVendingMachines && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.COUPONS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.COUPONS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className={`sidebar-navicons-subiconroot ${user?.roles?.includes("ROLE_EMPLOYEE") ? "subiconsec-active2" : ""}`}
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.COUPONS])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([ROUTES.COUPONS])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <CouponIcon
                            color={
                              isParentActive([ROUTES.COUPONS])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.COUPONS])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.coupons")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Reservations — vending-related, not for employees */}
                {/* {!user?.roles?.includes("ROLE_EMPLOYEE") &&
                 
                  showVendingMachines && (
                    <ListItem
                      disablePadding
                      onClick={() => navigate(ROUTES.RESERVATIONS)}
                    >
                      <ListItemButton
                        sx={{
                          pl: 4,
                          py: 0.5,
                          "&:hover": {
                            backgroundColor: "transparent",
                            opacity: 1,
                          },
                        }}
                        className={`sidebar-section-sub ${isActive(ROUTES.RESERVATIONS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                      >
                        <ListItemIcon
                          sx={{ background: iconSidebarBackground }}
                          className={`sidebar-navicons-subiconroot ${user?.roles?.includes("ROLE_EMPLOYEE") ? "subiconsec-active2" : ""}`}
                        >
                          <Box
                            sx={{
                              background: isParentActive([ROUTES.RESERVATIONS])
                                ? blueColor
                                : "transparent",
                              padding: "2px 6px 0px",
                              borderRadius: "6px",
                              transition: "background-color 0.3s ease",
                              "&:hover": {
                                backgroundColor: isParentActive([
                                  ROUTES.RESERVATIONS,
                                ])
                                  ? ""
                                  : "#FFF",
                              },
                            }}
                          >
                            <ReservationIcon
                              color={
                                isActive(ROUTES.RESERVATIONS)
                                  ? "#FFFFFF"
                                  : "#6F6F6F"
                              }
                            />
                          </Box>
                        </ListItemIcon>

                        <h2
                          className="sidebar-navitems-submain"
                          style={{
                            color: isParentActive([ROUTES.RESERVATIONS])
                              ? blueColor
                              : "",
                          }}
                        >
                          {t("sidebar.reservations")}
                        </h2>
                      </ListItemButton>
                    </ListItem>
                  )} */}

                {/* Returns — return-related, not for employees */}
                {!user?.roles?.includes("ROLE_EMPLOYEE") && showReturns && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.RETURNS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.RETURNS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className={`sidebar-navicons-subiconroot ${user?.roles?.includes("ROLE_EMPLOYEE") ? "subiconsec-active2" : ""}`}
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.RETURNS])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([ROUTES.RETURNS])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <ReturnIcon
                            color={
                              isParentActive([ROUTES.RETURNS])
                                ? "#FFFFFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>

                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.RETURNS])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.returns")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}

                {/* Users — common to both machine types */}
                {!user?.roles?.includes("ROLE_EMPLOYEE") && (
                  <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.USERS)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isActive(ROUTES.USERS) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot subiconsec-active2"
                      >
                        <Box
                          sx={{
                            background: isParentActive([ROUTES.USERS])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([ROUTES.USERS])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <UserIcon
                            color={
                              isParentActive([ROUTES.USERS])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([ROUTES.USERS])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.users")}
                      </h2>
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Collapse>
          </List>

          {/* Taxes Section — common to both machine types */}
          {!user?.roles?.includes("ROLE_EMPLOYEE") && showVendingMachines && (
            <ListItem
              className="sidebar-section-list"
              sx={{
                backgroundColor: sidebarBackground,
                ...(!open && { borderRadius: "8px", margin: "0 0px" }),
              }}
              disablePadding
              onClick={() => navigate(ROUTES.TAXES)}
            >
              <ListItemButton
                sx={{
                  ...(open
                    ? {
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                        padding: "4px 16px",
                      }
                    : {
                        minHeight: 40,
                        padding: "4px 0",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }),
                }}
                className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed" : ""} ${isParentActive([ROUTES.TAXES, ROUTES.TAXREPORT]) ? "active" : ""}`}
              >
                <ListItemIcon
                  className="sidebar-navicons-iconroot"
                  sx={
                    !open
                      ? {
                          minWidth: 0,
                          justifyContent: "center",
                          marginBottom: "2px",
                        }
                      : {}
                  }
                >
                  <TaxIcon
                    color={
                      isParentActive([ROUTES.TAXES, ROUTES.TAXREPORT])
                        ? blueColor
                        : "#6F6F6F"
                    }
                  />
                </ListItemIcon>
                <h2
                  className={
                    open
                      ? "sidebar-navitems-main"
                      : "sidebar-navitems-main-closed"
                  }
                  style={{
                    color: isParentActive([ROUTES.TAXES, ROUTES.TAXREPORT])
                      ? blueColor
                      : "",
                    fontFamily: "Satoshi",
                    ...(open
                      ? {
                          fontFamily: "Satoshi",
                        }
                      : {
                          fontSize: "10px",
                          marginTop: "2px",
                          textAlign: "center",
                          fontFamily: "Satoshi",
                          marginBottom: "2px",
                        }),
                  }}
                >
                  {t("sidebar.taxes")}
                </h2>
              </ListItemButton>
            </ListItem>
          )}

          {/* Maintenance Section — common to both machine types */}
          <List
            sx={{
              backgroundColor: sidebarBackground,
              ...(!open && { borderRadius: "8px", margin: "0 0px" }),
            }}
            className={`sidebar-section-list ${
              isParentActive(maintenanceRoutes) ? "active-parent" : ""
            }`}
          >
            <ListItem
              disablePadding
              onClick={() => {
                setOpenMaintenance(!openMaintenance);
              }}
            >
              <ListItemButton
                sx={{
                  ...(open
                    ? {
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                        padding: "4px 16px",
                      }
                    : {
                        minHeight: 56,
                        padding: "4px 0",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }),
                }}
                className={`sidebar-section-listitembutton ${
                  !open ? "sidebar-listitem-closed-dropdown" : ""
                } ${isParentActive(maintenanceRoutes) ? "active-parent" : ""}`}
              >
                <ListItemIcon
                  className="sidebar-navicons-iconroot"
                  sx={!open ? { minWidth: 0, justifyContent: "center" } : {}}
                >
                  <MaintenanceIcon
                    color={
                      isParentActive(maintenanceRoutes) ? blueColor : "#6F6F6F"
                    }
                  />
                </ListItemIcon>
                {open ? (
                  <>
                    <h2 className="sidebar-navitems-main">
                      {t("sidebar.maintenance")}
                    </h2>
                    {openMaintenance ? <ExpandLess /> : <ExpandMore />}
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "0px",
                    }}
                  >
                    <h2
                      className={
                        open
                          ? "sidebar-navitems-main"
                          : "sidebar-navitems-main-closed"
                      }
                      style={{
                        fontSize: "10px",
                        marginTop: "4px",
                        color: isParentActive([
                          ROUTES.SERVICE_REQUEST,
                          ROUTES.COMPLAINTS,
                          ROUTES.SPARES,
                          ROUTES.CARTPAGE,
                          ROUTES.ORDERS,
                          ROUTES.ORDER_DETAILS,
                        ])
                          ? blueColor
                          : "",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "40px",
                        textAlign: "center",
                        fontFamily: "Satoshi",
                        marginBottom: "2px",
                      }}
                    >
                      {t("sidebar.maintenance")}
                    </h2>
                    {openMaintenance ? (
                      <ExpandLess
                        sx={{
                          fontSize: "14px",
                          marginTop: "-2px",
                          marginBottom: open ? "0px" : "2px",
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          fontSize: "14px",
                          marginTop: "-2px",
                          marginBottom: open ? "0px" : "2px",
                        }}
                      />
                    )}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={openMaintenance} timeout="auto" unmountOnExit>
              <List component="div">
                <ListItem
                  disablePadding
                  onClick={() => navigate(ROUTES.SERVICE_REQUEST)}
                >
                  <ListItemButton
                    sx={{
                      pl: 4,
                      py: 0.5,
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                    }}
                    className={`sidebar-section-sub ${isParentActive([ROUTES.SERVICE_REQUEST, ROUTES.COMPLAINTS]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                  >
                    <ListItemIcon
                      sx={{
                        background: iconSidebarBackground,
                        borderRadius: "6px",
                        overflow: "hidden",
                      }}
                      className="sidebar-navicons-subiconroot subiconsec-active"
                    >
                      <Box
                        sx={{
                          background: isParentActive([
                            ROUTES.SERVICE_REQUEST,
                            ROUTES.COMPLAINTS,
                          ])
                            ? blueColor
                            : "transparent",
                          padding: "2px 6px 0px",
                          borderRadius: "6px",
                          transition: "background-color 0.3s ease",
                          "&:hover": {
                            backgroundColor: isParentActive([
                              ROUTES.SERVICE_REQUEST,
                              ROUTES.COMPLAINTS,
                            ])
                              ? ""
                              : "#FFF",
                          },
                        }}
                      >
                        <ServiceIcon
                          color={
                            isParentActive([
                              ROUTES.SERVICE_REQUEST,
                              ROUTES.COMPLAINTS,
                            ])
                              ? "#FFF"
                              : "#6F6F6F"
                          }
                        />
                      </Box>
                    </ListItemIcon>
                    <h2
                      className="sidebar-navitems-submain"
                      style={{
                        color: isParentActive([
                          ROUTES.SERVICE_REQUEST,
                          ROUTES.COMPLAINTS,
                        ])
                          ? blueColor
                          : "",
                      }}
                    >
                      {t("sidebar.service_request")}
                    </h2>
                  </ListItemButton>
                </ListItem>

                {/* <ListItem
                    disablePadding
                    onClick={() => navigate(ROUTES.SPARES)}
                  >
                    <ListItemButton
                      sx={{
                        pl: 4,
                        py: 0.5,
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }}
                      className={`sidebar-section-sub ${isParentActive([ROUTES.SPARES, ROUTES.CARTPAGE, ROUTES.ORDERS, ROUTES.ORDER_DETAILS]) ? "active" : ""} ${!open ? "active-sub" : ""}`}
                    >
                      <ListItemIcon
                        sx={{ background: iconSidebarBackground }}
                        className="sidebar-navicons-subiconroot subiconsec-active2"
                      >
                        <Box
                          sx={{
                            background: isParentActive([
                              ROUTES.SPARES,
                              ROUTES.CARTPAGE,
                              ROUTES.ORDERS,
                              ROUTES.ORDER_DETAILS,
                            ])
                              ? blueColor
                              : "transparent",
                            padding: "2px 6px 0px",
                            borderRadius: "6px",
                            transition: "background-color 0.3s ease",
                            "&:hover": {
                              backgroundColor: isParentActive([
                                ROUTES.SPARES,
                                ROUTES.CARTPAGE,
                                ROUTES.ORDERS,
                                ROUTES.ORDER_DETAILS,
                              ])
                                ? ""
                                : "#FFF",
                            },
                          }}
                        >
                          <SpareIcon
                            color={
                              isParentActive([
                                ROUTES.SPARES,
                                ROUTES.CARTPAGE,
                                ROUTES.ORDERS,
                                ROUTES.ORDER_DETAILS,
                              ])
                                ? "#FFF"
                                : "#6F6F6F"
                            }
                          />
                        </Box>
                      </ListItemIcon>
                      <h2
                        className="sidebar-navitems-submain"
                        style={{
                          color: isParentActive([
                            ROUTES.SPARES,
                            ROUTES.CARTPAGE,
                            ROUTES.ORDERS,
                            ROUTES.ORDER_DETAILS,
                          ])
                            ? blueColor
                            : "",
                        }}
                      >
                        {t("sidebar.spares")}
                      </h2>
                    </ListItemButton>
                  </ListItem> */}
              </List>
            </Collapse>
          </List>

          {/* Franchise Section */}
          {user?.roles?.includes("ROLE_SUPER_ADMIN") && (
            <ListItem
              className="sidebar-section-list"
              sx={{
                backgroundColor: sidebarBackground,
                ...(!open && { borderRadius: "8px", margin: "0 0px" }),
              }}
              disablePadding
              onClick={() => navigate(ROUTES.FRANCHISE)}
            >
              <ListItemButton
                sx={{
                  ...(open
                    ? {
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                        padding: "4px 16px",
                      }
                    : {
                        minHeight: 40,
                        padding: "4px 0",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: "transparent",
                          opacity: 1,
                        },
                      }),
                }}
                className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed" : ""} ${isParentActive([ROUTES.FRANCHISE, ROUTES.ADDFRANCHISE, ROUTES.PAYOUT]) ? "active" : ""}`}
              >
                <ListItemIcon
                  className="sidebar-navicons-iconroot"
                  sx={
                    !open
                      ? {
                          minWidth: 0,
                          justifyContent: "center",
                          marginBottom: "2px",
                        }
                      : {}
                  }
                >
                  <FranchiseIcon
                    color={
                      isParentActive([
                        ROUTES.FRANCHISE,
                        ROUTES.ADDFRANCHISE,
                        ROUTES.PAYOUT,
                      ])
                        ? blueColor
                        : "#6F6F6F"
                    }
                  />
                </ListItemIcon>
                <h2
                  className={
                    open
                      ? "sidebar-navitems-main"
                      : "sidebar-navitems-main-closed"
                  }
                  style={{
                    color: isParentActive([
                      ROUTES.FRANCHISE,
                      ROUTES.ADDFRANCHISE,
                      ROUTES.PAYOUT,
                    ])
                      ? blueColor
                      : "",
                    fontFamily: "Satoshi",
                    ...(open
                      ? {
                          fontFamily: "Satoshi",
                        }
                      : {
                          fontSize: "10px",
                          marginTop: "2px",
                          textAlign: "center",
                          fontFamily: "Satoshi",
                          marginBottom: "2px",
                        }),
                  }}
                >
                  {t("sidebar.franchise")}
                </h2>
              </ListItemButton>
            </ListItem>
          )}

          {/* Academy */}
          <ListItem
            className="sidebar-section-list"
            sx={{
              backgroundColor: sidebarBackground,
              ...(!open && { borderRadius: "8px", margin: "0 0px" }),
            }}
            disablePadding
            onClick={() => navigate(ROUTES.ACADEMY)}
          >
            <ListItemButton
              sx={{
                ...(open
                  ? {
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                      padding: "4px 16px",
                    }
                  : {
                      minHeight: 40,
                      padding: "4px 0",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                    }),
              }}
              className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed" : ""} ${isActive(ROUTES.ACADEMY) ? "active" : ""}`}
            >
              <ListItemIcon
                className="sidebar-navicons-iconroot"
                sx={
                  !open
                    ? {
                        minWidth: 0,
                        justifyContent: "center",
                        marginBottom: "2px",
                      }
                    : {}
                }
              >
                <Academy
                  color={isActive(ROUTES.ACADEMY) ? blueColor : "#6F6F6F"}
                />
              </ListItemIcon>
              <h2
                className={
                  open
                    ? "sidebar-navitems-main"
                    : "sidebar-navitems-main-closed"
                }
                style={{
                  color: isActive(ROUTES.ACADEMY) ? blueColor : "",
                  fontFamily: "Satoshi",
                  ...(open
                    ? {
                        fontFamily: "Satoshi",
                      }
                    : {
                        fontSize: "10px",
                        marginTop: "2px",
                        textAlign: "center",
                        fontFamily: "Satoshi",
                        marginBottom: "2px",
                      }),
                }}
              >
                {t("sidebar.academy")}
              </h2>
            </ListItemButton>
          </ListItem>

          {/* Help Section */}
          <ListItem
            className="sidebar-section-list"
            sx={{
              backgroundColor: sidebarBackground,
              ...(!open && { borderRadius: "8px", margin: "0 0px" }),
            }}
            disablePadding
            onClick={() => navigate(ROUTES.HELP)}
          >
            <ListItemButton
              sx={{
                ...(open
                  ? {
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                      padding: "4px 16px",
                    }
                  : {
                      minHeight: 40,
                      padding: "4px 0",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      "&:hover": {
                        backgroundColor: "transparent",
                        opacity: 1,
                      },
                    }),
              }}
              className={`sidebar-section-listitembutton ${!open ? "sidebar-listitem-closed" : ""} ${isActive(ROUTES.HELP) ? "active" : ""}`}
            >
              <ListItemIcon
                className="sidebar-navicons-iconroot"
                sx={
                  !open
                    ? {
                        minWidth: 0,
                        justifyContent: "center",
                        marginBottom: "2px",
                      }
                    : {}
                }
              >
                <HelpIcon
                  color={isParentActive([ROUTES.HELP]) ? blueColor : "#6F6F6F"}
                />
              </ListItemIcon>
              <h2
                className={
                  open
                    ? "sidebar-navitems-main"
                    : "sidebar-navitems-main-closed"
                }
                style={{
                  color: isParentActive([ROUTES.HELP]) ? blueColor : "",
                  fontFamily: "Satoshi",
                  ...(open
                    ? {
                        fontFamily: "Satoshi",
                      }
                    : {
                        fontSize: "10px",
                        marginTop: "2px",
                        textAlign: "center",
                        fontFamily: "Satoshi",
                        marginBottom: "2px",
                      }),
                }}
              >
                {t("sidebar.help")}
              </h2>
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </Box>
  );
}
